import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sun, CheckCircle2, Clock, AlertCircle, Calendar,
  ArrowRight, Target, Loader2, ChevronRight, Coffee
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const GREETING = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

export default function TodaysAgenda({ onNavigateToTasks }) {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAgenda = useCallback(async () => {
    try {
      const res = await api.get(token, '/analytics/todays-agenda');
      setData(res.data);
    } catch (err) {
      console.error('Agenda fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAgenda(); }, [fetchAgenda]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-gradient-to-br from-[#F9F7F5] to-white p-6">
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" /></div>
      </div>
    );
  }

  const tasks = data?.tasksDueToday || [];
  const overdueCount = data?.overdueCount || 0;
  const completedToday = data?.completedToday || 0;

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-gradient-to-br from-[#F9F7F5] to-white overflow-hidden" data-testid="todays-agenda">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sun className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-heading text-[#2C2420]">{GREETING()}, {user?.name?.split(' ')[0] || 'there'}</h2>
            </div>
            <p className="text-sm text-[#5D524D]">{data?.date || format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
          </div>
          <Coffee className="w-5 h-5 text-[#C6A88F]" />
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg bg-white border border-[#E6E0D9] p-3 text-center">
            <p className="text-xl font-bold text-[#2C2420]">{tasks.length}</p>
            <p className="text-[10px] text-[#5D524D] uppercase tracking-wider">Due Today</p>
          </div>
          <div className={cn("rounded-lg border p-3 text-center", overdueCount > 0 ? "bg-red-50 border-red-200" : "bg-white border-[#E6E0D9]")}>
            <p className={cn("text-xl font-bold", overdueCount > 0 ? "text-red-700" : "text-[#2C2420]")}>{overdueCount}</p>
            <p className="text-[10px] text-[#5D524D] uppercase tracking-wider">Overdue</p>
          </div>
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-center">
            <p className="text-xl font-bold text-green-700">{completedToday}</p>
            <p className="text-[10px] text-[#5D524D] uppercase tracking-wider">Done Today</p>
          </div>
        </div>
      </div>

      {/* Tasks Due Today */}
      <div className="px-6 pb-4">
        {tasks.length === 0 ? (
          <div className="text-center py-4 rounded-lg bg-green-50/50 border border-green-100">
            <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-sm text-green-700 font-medium">All clear for today!</p>
            <p className="text-xs text-green-600">No tasks due. Focus on clearing your overdue items.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-medium text-[#8C8078] uppercase tracking-wider">Today's Tasks</p>
            {tasks.slice(0, 5).map(task => (
              <div
                key={task.id}
                className="flex items-center gap-3 rounded-lg bg-white border border-[#E6E0D9] px-3 py-2.5 hover:shadow-sm transition-all"
                data-testid={`agenda-task-${task.id}`}
              >
                <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2C2420] truncate">{task.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge className={cn("text-[10px]", task.contextType === 'lead' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700')}>
                      {task.contextName}
                    </Badge>
                    <span className="text-[10px] text-[#8C8078]">{task.owner || task.assignedTo || ''}</span>
                  </div>
                </div>
                <Badge className={cn("text-[10px] shrink-0",
                  task.priority?.toLowerCase() === 'high' ? 'bg-red-100 text-red-700' :
                  task.priority?.toLowerCase() === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                )}>{task.priority}</Badge>
              </div>
            ))}
            {tasks.length > 5 && (
              <p className="text-xs text-[#8C8078] text-center py-1">+{tasks.length - 5} more tasks</p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {onNavigateToTasks && (
        <div className="px-6 pb-4">
          <Button
            variant="outline"
            className="w-full text-sm"
            onClick={onNavigateToTasks}
            data-testid="agenda-view-all-tasks"
          >
            View All Tasks <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
